import axios from 'axios';
import { startOfHour, subDays, subHours } from 'date-fns';

import appSettings from '../app-settings';

import contacts from '../data/contacts/index.json';
import count from '../data/contacts/count.json';
import countDonorspring from '../data/contacts/count-donorspring.json';
import countDonorspringSubscribed from '../data/contacts/count-donorspring-subscribed.json';
import donations from '../data/donations.json';
import reports from '../data/reports.json';
import stripeDetails from '../data/donations/stripe-details.json';
import usage from '../data/usage.json';
import recommendedTasks from '../data/recommended-tasks.json';
import suggested from '../data/suggested.json';
import suggestedTemplate from '../data/suggested.html?raw';
import template from '../data/template.json';
import segments from '../data/segments.json';
import images from '../data/images.json';

const mocks = {};

function addMock(url, data) {
  mocks[appSettings.baseUrl + url] = data;
}

const isUrlMocked = (url) => url in mocks;

const getUrl = (url, params) => {
  if (!params) return url;
  return `${url}?${new URLSearchParams(params).toString()}`;
};

const getMockError = (config) => {
  const mockError = new Error();
  mockError.mockData = mocks[getUrl(config.url, config.params)];
  mockError.config = config;
  return Promise.reject(mockError);
};

const isMockError = (error) => !!error.mockData;

const getMockResponse = (mockError) => {
  const { mockData, config } = mockError;

  // Handle mocked success
  return Promise.resolve({
    data: mockData,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    isMock: true,
  });
};

axios.interceptors.request.use((config) => {
  if (isUrlMocked(getUrl(config.url, config.params))) {
    return getMockError(config);
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isMockError(error)) {
      return getMockResponse(error);
    }
    return Promise.reject(error);
  },
);

export function enableMocking() {
  const mockingEnabled = Object.keys(mocks).length;
  if (mockingEnabled) return;

  // Add recent createdAt timestamps to donations
  let updatedDonations = donations.map((donation) => {
    donation.createdAt = subHours(new Date(), donation.hoursAgo).getTime() / 1000;
    return donation;
  });

  // Add recent sendAt timestamps to repots
  const updatedReports = reports.map((report) => {
    report.sendAt = startOfHour(subHours(new Date(), report.hoursAgo).getTime()) / 1000;
    return report;
  });

  // Add recent dates to usage
  const updatedUsage = usage
    .map((item, index) => {
      const date = subDays(new Date(), index + 1);
      item.date = date.toISOString().split('T')[0];
      return item;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  addMock('/contacts?page=1&limit=10&sort_by=updated_at&sort_direction=desc', contacts);
  addMock(
    '/contacts?page=1&limit=10&sort_by=updated_at&sort_direction=desc&segment_ids=ydx6gti&onboarding_campaign_id=9clmg3g&match=all',
    contacts,
  );
  addMock('/contacts/count?segment_ids=ydx6gti&onboarding_campaign_id=9clmg3g&match=all', { count: 10 });
  addMock('/contacts/count', count);
  addMock('/contacts/count?unsubscribed=false', count);
  addMock('/contacts/count?import_filename=donorspringupload_&opted_in=any', countDonorspring);
  addMock('/contacts/count?unsubscribed=false&import_filename=donorspringupload_', countDonorspringSubscribed);
  addMock('/reports', updatedReports);

  // Image library. Keys must match the exact query string getGalleryImages builds.
  addMock('/campaigns/images?page=1&perPage=24', images);
  addMock('/campaigns/images?page=1&perPage=24&filter=needs-description', {
    ...images,
    images: images.images.filter((image) => !image.description.trim()),
    total: images.images.filter((image) => !image.description.trim()).length,
  });
  addMock('/donations?dashboard=true', updatedDonations);
  addMock('/donations', updatedDonations);
  addMock('/donations/stripe-details', stripeDetails);
  addMock('/accounts/usage', updatedUsage);
  addMock('/recommended-tasks?status=pending&limit=4', recommendedTasks);
  addMock('/notifications/preferences', {
    preferences: {
      'weekly-campaign-scheduling': true,
      'review-suggested-draft': true,
      message: true,
      'monthly-update-reminder': true,
      'review-potential-major-donors': true,
    },
  });
  addMock('/campaigns/suggested', suggested);
  // Download URL will usually be from S3, but we mock it here
  addMock('/email-templates/VaSdfqRRVFkhGPtrePt8g/1?suggested=true', {
    ...template,
    downloadUrl: `${appSettings.baseUrl}/email-templates/VaSdfqRRVFkhGPtrePt8g/1/download?suggested=true`,
  });
  addMock('/email-templates/VaSdfqRRVFkhGPtrePt8g/1/download?suggested=true', suggestedTemplate);
  addMock('/segments', segments);
}
